import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
// MUI components
import { Typography } from "@mui/material";
// components
import SelectDropdown from "../../components/SelectDropdown";
import Table from "../../components/Table";
import ErrorText from "../../components/ErrorText";
import { ShimmerComponent } from "../../components/Shimmer/ShimmerComponent";
import BouncingLogo from "../../components/Shimmer/BouncingLogo";
import { ToggleComponent } from "../../components/Toggle";
// constants, functions, 
import { BEFORE_FIVE_WEEKS_KEY_INSIGHTS, CURRENT_WEEK_YTD_INSIGHTS, fetchInsightsSummary, fetchLeadTime, fetchOppsStatus, fetchOppsYTD, LEADTIME_KEY_INSIGHTS, OppsInsightTexts, oppsTableOneHeaders, OppsTableOneRows, oppsTableThreeHeaders, OppsTableThreeRows, oppsTableTwoHeaders, OppsTableTwoRows, YTD_KEY_INSIGHTS } from "../OpportunitiesInsights/services/OpportunitiesInsightsServices";
import { PDFNames } from "../../constants/utils";
import selectors from "../../modules/selectors/datePickerSelectors";
// styles 
import branchPerformanceStyles from "../../styles/branchPerformanceStyles";
import NoData from "../../components/NoData";
import { PDFButton } from "../../components/PdfComponent/PDFButton";
import { filterValidation, previousWeekExtractor } from "../../data/branchPerformanceData";

const OpportunitiesInsights = () => {
  const pdfRef = useRef();
  const { userStores, userData } = useSelector((state) => state.auth);
  const options = useMemo(() => {
    const stores = userStores.map(store => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter(store => store.value === 1);
    if (GaleMarkets?.length) {
      return [GaleMarkets[0], ...stores.filter(store => store.value !== 1).sort((a, b) => a.label.localeCompare(b.label))]
    } else {
      return [...stores.filter(store => store.value !== 1).sort((a, b) => a.label.localeCompare(b.label))];
    }
  }, [userStores]);
  const tableId = userData?.data?.data_sources?.find(data => data?.name === "branch_performance_opps_insights")?.identifier;
  const { transformedDataWithDate } = useSelector(selectors.datePickerCombinedData);
  const [storeId, setStoreId] = useState(options?.[0]?.value);
  const [resetStore, setResetStore] = useState(false);
  const tableOneFilter = previousWeekExtractor(transformedDataWithDate, 5)?.filter;// updated as base logic upgraded in function itself to handle previous week and tuesday 
  const tableTwoFilterOne = previousWeekExtractor(transformedDataWithDate, 5)?.filter; // updated as base logic upgraded in function itself to handle previous week and tuesday  
  const tableTwoFilterTwo = previousWeekExtractor(transformedDataWithDate)?.filter; // updated as base logic upgraded in function itself to handle previous week and tuesday 
  const tableThreeFiltersData = previousWeekExtractor(transformedDataWithDate, 5);// updated as base logic upgraded in function itself to handle previous week and tuesday 
  const tableThreeUpdatedFilter = tableThreeFiltersData?.filter;
  const [tableOneHeaders, setTableOneHeaders] = useState([]);
  const [tableOneRowData, setTableOneRowData] = useState([]);
  const [tableTwoHeaders, setTableTwoHeaders] = useState([]);
  const [tableTwoRowData, setTableTwoRowData] = useState([]);
  const [tableThreeHeaders, setTableThreeHeaders] = useState([]);
  const [tableThreeRowData, setTableThreeRowData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const handleStoreIdChange = (store) => {
    setStoreId(store)
  }

  const handleResetForStore = () => {
    setResetStore(true)
  }

  const {
    data: insightSummaryData,
    mutate: insightSummaryMutation,
    isLoading: isLoadingInsightSummary,
  } = useMutation({
    mutationFn: fetchInsightsSummary
  })

  const {
    data: oppsLeadTimeData,
    mutate: oppsLeadTimeMutation,
    isLoading: isLoadingOppsLeadTime,
  } = useMutation({
    mutationFn: fetchLeadTime
  })

  const {
    data: oppsStatusData,
    mutate: oppsStatusMutation,
    isLoading: isLoadingOppsTime,
  } = useMutation({
    mutationFn: fetchOppsStatus
  })

  const {
    data: oppsStatusYTDData,
    mutate: oppsStatusYTDMutation,
    isLoading: isLoadingOppsYTD,
  } = useMutation({
    mutationFn: fetchOppsYTD
  })

  useEffect(() => {
    if (transformedDataWithDate?.length > 0) {
      const tableOneCurrentWeek = previousWeekExtractor(transformedDataWithDate)?.filter;
      setTableOneHeaders(oppsTableOneHeaders(transformedDataWithDate));
      setTableTwoHeaders(oppsTableTwoHeaders(transformedDataWithDate));
      setTableThreeHeaders(oppsTableThreeHeaders(transformedDataWithDate));
      if(!filterValidation(tableOneCurrentWeek) && !filterValidation(tableOneFilter) && !filterValidation(tableTwoFilterOne) && !filterValidation(tableTwoFilterTwo)){
        console.log("Filter values setting error in opportunities insights--->");
      }
      // added loading check and filter for optimising the API calls so that it should be called only once after reload also
      !isLoadingInsightSummary && filterValidation(tableOneCurrentWeek) && filterValidation(tableOneFilter) && insightSummaryMutation({ storeId, tableOneCurrentWeek, tableOneFilter, tableId, YTD_KEY_INSIGHTS});
      !isLoadingOppsLeadTime && filterValidation(tableTwoFilterOne) && filterValidation(tableTwoFilterTwo) && oppsLeadTimeMutation({ storeId, tableTwoFilterOne, tableTwoFilterTwo, tableId, LEADTIME_KEY_INSIGHTS});
      !isLoadingOppsTime && filterValidation(tableThreeUpdatedFilter) && oppsStatusMutation({ storeId, tableThreeUpdatedFilter, tableId, BEFORE_FIVE_WEEKS_KEY_INSIGHTS});
      !isLoadingOppsYTD && filterValidation(tableTwoFilterTwo) && oppsStatusYTDMutation({ storeId, tableTwoFilterTwo, tableId, CURRENT_WEEK_YTD_INSIGHTS});
      setLoading(false);
    }
  }, [storeId, transformedDataWithDate]);

  useEffect(() => {
    if (insightSummaryData && insightSummaryData?.data?.opps_insights_summary_query?.result?.length > 0) {
      setTableOneRowData(OppsTableOneRows(insightSummaryData?.data, transformedDataWithDate));
    }
  }, [storeId, insightSummaryData]);


  useEffect(() => {
    if (oppsLeadTimeData?.data?.opps_insights_lead_time_query?.result && oppsLeadTimeData?.data?.opps_insights_lead_time_query?.result?.length > 0) {
      setTableTwoRowData(OppsTableTwoRows(oppsLeadTimeData?.data, transformedDataWithDate));
    }
  }, [storeId, oppsLeadTimeData]);

  useEffect(() => {
    if (oppsStatusData && oppsStatusData?.data?.opps_insights_YTD_five_weeks_before_query?.result?.length > 0
      && oppsStatusYTDData && oppsStatusYTDData?.data?.opps_insights_YTD_current_week_query?.result?.length > 0) {
      setTableThreeRowData(OppsTableThreeRows(oppsStatusData?.data, oppsStatusYTDData?.data, transformedDataWithDate));
    }
  }, [storeId, oppsStatusData, oppsStatusYTDData]);
  
  return ((userStores?.length > 0) ?
    <div ref={pdfRef} style={branchPerformanceStyles?.insightsContainer}>
        <div style={{ width: "90%", textAlign: 'left', margin: 'auto', fontSize: "1.5rem", fontWeight: "500", lineHeight: "1.6", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{OppsInsightTexts?.headline}</span>
        </div>
      <div style={branchPerformanceStyles?.insightsDropdwonContainer}>
        <div>
          <SelectDropdown
            options={options}
            label={OppsInsightTexts?.branchDropDownLabel}
            resetValues={resetStore}
            handleResetValues={handleResetForStore}
            singleSelectDefaultValue={options[0]?.value}
            width={'200px'}
            handleFilterChange={handleStoreIdChange}
            marginExist={false}
            disabled={isLoading || isLoadingOppsYTD || isLoadingInsightSummary || isLoadingOppsLeadTime || isLoadingOppsTime}
          />
        </div>
        <PDFButton
          disabled={isLoading || isLoadingOppsYTD || isLoadingInsightSummary || isLoadingOppsLeadTime || isLoadingOppsTime}
          pdfRef={pdfRef} pdfName={PDFNames.oppsInsights}
        />
      </div>
      {isLoading || isLoadingOppsYTD || isLoadingInsightSummary || isLoadingOppsLeadTime || isLoadingOppsTime ? (
        <ShimmerComponent updatedStyle={{ width: '90%', height: '100%', backgroundSize: '2000px 800px', border: '0px', margin: "auto", padding: "0 20px"}} >
            <BouncingLogo height='72vh' text='Loading...' />
        </ShimmerComponent>) :
        <>
          <div style={branchPerformanceStyles?.insightsOppsSummaryCont}>
            <Typography variant="h6" align='left' >
              {OppsInsightTexts?.summaryText}
            </Typography>
            {insightSummaryData?.data?.opps_insights_summary_query?.result && insightSummaryData?.data?.opps_insights_summary_query?.result?.length > 0 ? <Table boldHeaders={true} boldDataColumns={['_Heading']} data={tableOneRowData} columns={tableOneHeaders} applyStyleToLastRow /> : <NoData />}
            <Typography variant="h6" align='left' >
              {OppsInsightTexts?.avgAppointText}
            </Typography>
            {oppsLeadTimeData?.data?.opps_insights_lead_time_query?.result?.length > 0 ? <Table boldHeaders={true} boldDataColumns={['_Heading']} data={tableTwoRowData} columns={tableTwoHeaders}
              getCellProps={cellInfo => {
                if (!['_Heading']?.includes(cellInfo.column.id)) {
                  return {
                    style: { ...cellInfo?.value > 5 ? { backgroundColor: '#FFE9E9' } : {} } // value configuration for the palette color(single color palette)
                  }
                } else { return { style: {} } }
              }}
            /> : <NoData />}
          </div>
          <div style={branchPerformanceStyles?.insightsOppsSummaryCont}>
            <Typography variant="h6" align='left' >
              {OppsInsightTexts?.oppsByStatusText}
            </Typography>
            {oppsStatusData?.data?.opps_insights_YTD_five_weeks_before_query?.result?.length > 0 ? <Table boldHeaders={true} boldDataColumns={['_Heading']} data={tableThreeRowData} columns={tableThreeHeaders} applyStyleToLastRow /> : <NoData />}
          </div></>}
    </div> : <div style={branchPerformanceStyles?.errorContainerStyle}>
      <div style={branchPerformanceStyles?.errorSubcont}>
        <div style={branchPerformanceStyles?.errorTextContainer}>
          <ErrorText reason='No branches assigned to user' />
        </div>
      </div>
    </div>)
}

export default OpportunitiesInsights;